import { type CalculatorDef, type CalcResult, checkFinite, err, num, ok } from './types';

/** Reference temperature all alcoholmeters are calibrated to, °C. */
export const REFERENCE_TEMP_C = 20;

/**
 * Approximate shift in apparent %ABV per °C away from 20 °C.
 *
 * The true relationship is non-linear and depends on strength as well as temperature;
 * official work uses the OIML alcoholometric tables. This single coefficient is the
 * widely-used field approximation and is reasonable for spirit-range readings near room
 * temperature. It is not a substitute for the tables — see {@link correctForTemperature}.
 */
export const CORRECTION_PER_DEGREE = 0.33;

/** Range in which the linear approximation stays sensible. Outside it we refuse. */
export const VALID_TEMP_RANGE = { min: 0, max: 40 } as const;
export const VALID_READING_RANGE = { min: 20, max: 96 } as const;

export type TempCorrectionResult = {
  /** Strength corrected to 20 °C, %ABV. */
  correctedAbv: number;
  /** Size of the correction applied, percentage points (signed). */
  correction: number;
  /** Degrees away from 20 °C (signed). */
  deviation: number;
}

/**
 * Correct an alcoholmeter reading taken at some temperature back to the 20 °C reference.
 *
 * A warm spirit is less dense, so the float sits lower and the meter reads *high*; a cold
 * spirit reads low. Hence:
 *
 *     corrected = reading - 0.33 x (temperature - 20)
 *
 * **This is an approximation.** It is linear in temperature and ignores strength entirely,
 * whereas the real correction does both. Expect a few tenths of a percent of error near
 * the edges of the accepted range, and more outside it — which is why readings outside
 * 20-96 %ABV or 0-40 °C are rejected rather than silently extrapolated. For anything with
 * legal or commercial weight, use the official OIML tables, not this.
 *
 * @param reading     What the alcoholmeter says, %ABV.
 * @param temperature Temperature of the spirit at the time of reading, °C.
 */
export function correctForTemperature(
  reading: number,
  temperature: number,
): CalcResult<TempCorrectionResult> {
  const invalid = checkFinite({ reading, temperature });
  if (invalid) return { ok: false, error: invalid };

  if (reading < VALID_READING_RANGE.min || reading > VALID_READING_RANGE.max) {
    return err('error.readingOutOfRange', { ...VALID_READING_RANGE }, 'reading');
  }
  if (temperature < VALID_TEMP_RANGE.min || temperature > VALID_TEMP_RANGE.max) {
    return err('error.tempOutOfRange', { ...VALID_TEMP_RANGE }, 'temperature');
  }

  const deviation = temperature - REFERENCE_TEMP_C;
  const correction = -CORRECTION_PER_DEGREE * deviation;
  const correctedAbv = reading + correction;

  return ok({ correctedAbv, correction, deviation });
}

export const tempCorrectionCalculator: CalculatorDef = {
  id: 'tempCorrection',
  notesKey: 'notes.tempApproximation',
  fields: [
    { name: 'reading', unit: 'percent', min: 20, max: 96, step: 0.1, defaultValue: 52 },
    { name: 'temperature', unit: 'celsius', min: 0, max: 40, step: 0.5, defaultValue: 25 },
  ],
  outputs: [
    { name: 'correctedAbv', unit: 'percent', precision: 1, primary: true },
    { name: 'correction', unit: 'percent', precision: 2 },
    { name: 'deviation', unit: 'celsius', precision: 1 },
  ],
  compute: (i) => correctForTemperature(num(i, 'reading'), num(i, 'temperature')),
};
